import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

function VideoApp() {
  const [videos, setVideos] = useState([]);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const [files, setFiles] = useState([]);

  // Load MD files
  useEffect(() => {
    fetch("https://siw.sg/caiintro/md-files", { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setFiles(data))
      .catch((err) => console.error("Error loading MD files:", err));
  }, []);

  // Load videos after login
  useEffect(() => {
    fetch("https://siw.sg/caiintro/videos", { credentials: "include" })
      .then((res) => {
        if (res.status === 401) {
          setLoggedIn(false);
          return [];
        }
        setLoggedIn(true);
        return res.json();
      })
      .then((data) => setVideos(data))
      .catch((err) => console.error("Error loading videos:", err));
  }, []);

  // Load selected video
  const loadVideo = (filename) => {
    fetch(`https://siw.sg/caiintro/watch/${encodeURIComponent(filename)}`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setCurrentVideo(data.url))
      .catch((err) => console.error("Error loading video:", err));
  };

  // Login screen
  if (!loggedIn) {
    return (
      <motion.div
        style={{ padding: "20px", textAlign: "center", background: "white" }}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
      >
        <h1 style={{ color: "black" }}>🎬 Theory Videos & Lab Material</h1>
        <a href="https://siw.sg/auth/google?redirectTo=CAI-Intro">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{
              padding: "10px 20px",
              fontSize: "18px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
          >
            Login with Google
          </motion.button>
        </a>
      </motion.div>
    );
  }

  return (
    <div style={{ padding: "20px", background: "white", minHeight: "100vh" }}>
      <div style={{ textAlign: "right", marginBottom: "20px" }}>
        <a href="https://siw.sg/logout">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            style={{
              padding: "8px 16px",
              borderRadius: "10px",
              background: "#ff4b5c",
              color: "white",
              border: "none",
              cursor: "pointer",
            }}
          >
            Logout
          </motion.button>
        </a>
      </div>

      {/* Two-column layout */}
      <div
        style={{
          display: "flex",
          gap: "40px",
          alignItems: "flex-start",
        }}
      >
        {/* Left Column - Videos */}
        <div style={{ flex: 1 }}>
          <h2 style={{ color: "black", textAlign: "center" }}>🎬 Theory Videos</h2>
          <AnimatePresence>
            {currentVideo ? (
              <motion.div
                key="video"
                initial={{ opacity: 0, x: 80 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -80 }}
                transition={{ duration: 0.5 }}
              >
                <video
                  width="100%"
                  height="360"
                  controls
                  autoPlay
                  controlsList="nodownload"
                  onContextMenu={(e) => e.preventDefault()}
                  style={{
                    borderRadius: "12px",
                    boxShadow: "0px 4px 20px rgba(0,0,0,0.3)",
                  }}
                >
                  <source src={currentVideo} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <br />
                <motion.button
                  onClick={() => setCurrentVideo(null)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  style={{
                    marginTop: "15px",
                    padding: "10px 20px",
                    borderRadius: "10px",
                    background: "#027f89",
                    backgroundImage:
                      "linear-gradient(315deg, #6a11cb 0%, #2575fc 74%)",
                    color: "white",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  ⬅ Back to list
                </motion.button>
              </motion.div>
            ) : (
              <motion.ul
                key="list"
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                style={{ listStyle: "none", padding: 0 }}
              >
                {videos.map((file, idx) => (
                  <motion.li
                    key={idx}
                    whileHover={{ scale: 1.05, x: 10 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    style={{
                      margin: "10px 0",
                      padding: "10px 15px",
                      borderRadius: "10px",
                      background: "#027f89",
                      color: "white",
                      cursor: "pointer",
                      fontWeight: "bold",
                      boxShadow: "0px 3px 10px rgba(0,0,0,0.2)",
                    }}
                    onClick={() => loadVideo(file)}
                  >
                    {file}
                  </motion.li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>

        {/* Right Column - Lab Material */}
        <div style={{ flex: 1 }}>
          <h2 style={{ color: "black", textAlign: "center" }}>📄 Lab Material</h2>
          <motion.ul
            key="list"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            style={{ listStyle: "none", padding: 0 }}
          >
            {files.map((file, idx) => (
              <motion.li
                key={idx}
                whileHover={{ scale: 1.05, x: 10 }}
                transition={{ type: "spring", stiffness: 300 }}
                style={{
                  margin: "10px 0",
                  padding: "10px 15px",
                  borderRadius: "10px",
                  background: "#027f89",
                  color: "white",
                  cursor: "pointer",
                  fontWeight: "bold",
                  boxShadow: "0px 3px 10px rgba(0,0,0,0.2)",
                  textAlign: "center",
                }}
              >
                <a
                  href={`https://siw.sg/caiintro/md/${encodeURIComponent(file)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "white", textDecoration: "none" }}
                >
                  {file}
                </a>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </div>
    </div>
  );
}

export default VideoApp;
